import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { AddCircleOutline, Delete, Edit, Visibility } from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  ListItemIcon,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { useMap } from "@vis.gl/react-google-maps"
import { MuiColorInput } from "mui-color-input"
import { FC, memo, useState } from "react"
import { useDispatch } from "react-redux"
import { useAppSelector } from "src/app/hooks"
import { setEditMode } from "src/app/slices/dispatcherRoutePlan.slice"
import { MenuWrapper } from "../../shared-module/components"
import { useDeleteRouteMutation, useGetRouteListQuery } from "../apis"
import { BoundTypeEnum } from "../constants"
import { RoutePlanningModel } from "../models"
import { DispatcherRouteForm } from "./DispatcherRouteForm"

export const DispatcherRouteList: FC = () => {
  const [open, setOpen] = useState(false)

  const { data: routeList, isLoading } = useGetRouteListQuery({})

  const handleOpen = () => {
    setOpen(true)
  }

  const onCreateRoute = () => {
    setOpen(false)
  }

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return
      }
      setOpen(open)
    }

  if (isLoading) {
    /* TODO: add skeleton */
    return <>Loading...</>
  }

  return (
    <Box
      {...uiSurface("UI-DSP-042")}
      sx={{
        paddingY: 1,
        height: "100%",
      }}
    >
      <Box
        sx={{
          textAlign: "right",
          marginBottom: 1,
        }}
      >
        <Button
          data-testid="route-add-btn"
          color="primary"
          variant="contained"
          endIcon={<AddCircleOutline />}
          onClick={() => {
            handleOpen()
          }}
        >
          Add New Route
        </Button>
      </Box>
      <Collapse in={open}>
        <Box
          onClick={() => toggleDrawer(false)}
          sx={{
            padding: 1,
          }}
        >
          <DispatcherRouteForm
            onSubmit={onCreateRoute}
            onCancel={() => setOpen(false)}
          />
        </Box>
      </Collapse>
      <Stack
        spacing={1}
        height={`calc(100% - ${38}px)`}
        overflow={"auto"}
        padding={1}
      >
        {(routeList ?? []).map((route: RoutePlanningModel) => {
          return <RouteView key={route.id} route={route} />
        })}
      </Stack>
    </Box>
  )
}

const RouteView: FC<{ route: RoutePlanningModel }> = memo(({ route }) => {
  const dispatch = useDispatch()
  const isEdit = useAppSelector(
    (state) => state.routePlanning.editMode?.routeId === route.id,
  )

  const [deleteRouteFn, { isLoading: isDeleteLoading }] =
    useDeleteRouteMutation()

  const onRouteDelete = async () => {
    try {
      await deleteRouteFn(route.id).unwrap()
    } catch (error) {
      console.error("Failed to delete route:", error)
    }
  }

  const setEditView = (isEdit: boolean) => {
    dispatch(
      setEditMode(
        isEdit
          ? {
              editView: isEdit,
              routeId: route.id,
              boundType: route.bounds.find(
                (e) => e.boundType === BoundTypeEnum.user_drawn_area,
              )?.boundType as any,
            }
          : null,
      ),
    )
  }

  const changeColor = (color: string) => {}

  return isEdit ? (
    <Paper
      data-testid={`route-item-edit-${route.id}`}
      elevation={1}
      variant="outlined"
      sx={{
        padding: 1.5,
      }}
    >
      <DispatcherRouteForm
        routeDetail={route}
        onSubmit={() => setEditView(false)}
        onCancel={() => setEditView(false)}
      />
    </Paper>
  ) : (
    <Paper
      data-testid={`route-item-${route.id}`}
      elevation={1}
      variant="outlined"
      sx={{
        padding: 1.5,
        borderRadius: 2,
        borderWidth: 2,
      }}
    >
      <Stack
        direction={"row"}
        spacing={1}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <MuiColorInput
            data-testid={`route-color-${route.id}`}
            isAlphaHidden={true}
            variant="standard"
            format="hex"
            value={route.color}
            onChange={changeColor}
            sx={{
              width: 20,
              "& .MuiInput-root": {
                "&::before": {
                  content: "none",
                },
                "& .notranslate": {
                  display: "none",
                },
                "& .MuiInputBase-input": { display: "none" },
                "& .MuiInputAdornment-root": { margin: 0 },
                "& .MuiButton-root": {
                  width: 20,
                  height: 20,
                },
              },
            }}
          />
          <Typography data-testid={`route-name-${route.id}`} variant="subtitle1">{route.name}</Typography>
        </Stack>
        <Box
          sx={{
            marginLeft: "auto",
          }}
        >
          <Stack direction={"row"} alignItems={"center"}>
            <IconButton
              data-testid={`route-edit-btn-${route.id}`}
              onClick={() => setEditView(true)}
              size="small"
              color="primary"
            >
              <Edit />
            </IconButton>
            {isDeleteLoading ? (
              <IconButton>
                <CircularProgress size={24} />
              </IconButton>
            ) : (
              <MenuWrapper
                useIconButton={true}
                menuProps={{ slotProps: { paper: uiPaper("UI-DSP-044") } }}
              >
                <MenuItem data-testid={`route-delete-btn-${route.id}`} onClick={onRouteDelete}>
                  <ListItemIcon>
                    <Delete fontSize="small" />
                  </ListItemIcon>
                  Delete
                </MenuItem>
                <MenuItem data-testid={`route-toggle-visibility-btn-${route.id}`}>
                  <ListItemIcon>
                    <Visibility fontSize="small" />
                  </ListItemIcon>
                  Show/Hide
                </MenuItem>
              </MenuWrapper>
            )}
          </Stack>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1}>
        {route.bounds.map((bound) =>
          bound.values.map((postalCode: any) => {
            return <PostalCodeChip postalCode={postalCode.code} />
          }),
        )}
      </Stack>
    </Paper>
  )
})

const PostalCodeChip: FC<{ postalCode: string }> = ({ postalCode }) => {
  const map = useMap()
  const location = useAppSelector(
    (state) => state.routePlanning.postalCodeStyles?.[postalCode]?.location,
  )

  const panToLocation = () => {
    if (!map) return
    map.panTo(location)
    map.setZoom(12)
  }

  return (
    <Chip
      data-testid={`route-postal-code-${postalCode}`}
      onClick={panToLocation}
      clickable
      color="primary"
      label={`${postalCode}`}
      variant="outlined"
    />
  )
}
